import React, { useEffect, useState } from 'react'
import BreadCrumbs from '../components/BreadCrumbs'
import { callGetWebservice } from '../hooks/api';
import { useLocation } from 'react-router-dom';
import { getQueryParam } from '../utils/Utilities';
import { Helmet } from 'react-helmet-async';

const Stores = () => {

    const [outlets, setOutlets] = useState([]);
    const location = useLocation();

    useEffect(() => {

        let requestPacket = {};
        const home_outlet = getQueryParam(location, 'home_outlet');
        if (home_outlet) {
            requestPacket.home_outlet = home_outlet;
        }

        callGetWebservice('outlets', requestPacket, (response) => {
            handleOutletWebservice(response);
        });

    }, [location])

    const handleOutletWebservice = (response) => {
        setOutlets(response.data.outlets);
    }
    const formatTimeAMPM = (timeStr) => {
        if (!timeStr || timeStr === 'N/A' || timeStr === 'Closed') return timeStr;
        let [hours, minutes] = timeStr.split(':').map(Number);
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        return `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
    };

    const getOutletStatus = (outletHours) => {
        if (!outletHours || outletHours.length === 0) return { isOpen: false, open: 'N/A', close: 'N/A' };

        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const now = new Date();
        const currentDay = days[now.getDay()];

        const currentHours = now.getHours();
        const currentMinutes = now.getMinutes();
        const currentTimeInMinutes = currentHours * 60 + currentMinutes;

        const todayHours = outletHours.find(h => h.weekday === currentDay && h.is_active === 1);
        if (!todayHours) return { isOpen: false, open: 'Closed', close: 'Closed' };

        const [oH, oM] = todayHours.open_time.split(':').map(Number);
        const [cH, cM] = todayHours.close_time.split(':').map(Number);

        const openMinutes = oH * 60 + oM;
        let closeMinutes = cH * 60 + cM;

        let isOpen = false;

        if (closeMinutes === 0) closeMinutes = 24 * 60;

        if (closeMinutes < openMinutes) {
            isOpen = currentTimeInMinutes >= openMinutes || currentTimeInMinutes <= closeMinutes;
        } else {
            isOpen = currentTimeInMinutes >= openMinutes && currentTimeInMinutes <= closeMinutes;
        }

        return {
            isOpen,
            open: formatTimeAMPM(todayHours.open_time),
            close: formatTimeAMPM(todayHours.close_time)
        };
    };

    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';

    return (
        <>
            <Helmet>
                <title>Store Locations &amp; Outlets | Online Store</title>
                <meta name="description" content="Find our store locations, contact details, opening hours, and addresses. Visit us at any of our outlets." />
                {currentOrigin && <link rel="canonical" href={`${currentOrigin}/store`} />}
            </Helmet>

            {/* Modern Hero Section */}
            <section className="store-hero-section">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-xl-8 col-lg-10 col-md-12">
                            <h1 className="animate__animated animate__fadeInDown text-white">Our Store Locations</h1>
                            <p className="animate__animated animate__fadeInUp animate__delay-1s">
                                Visit us at any of our premium locations across the city.
                                Quality taste and exceptional service, just around the corner.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="middle bg-light">
                <div className="container">
                    <div className="row">
                        {outlets.map((outlet, index) => {
                            const status = getOutletStatus(outlet.outlet_hours);
                            return (
                                <div key={index} className="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-5 animate__animated animate__fadeInUp" style={{ animationDelay: `${index * 0.1}s` }}>
                                    <div className="store-card-modern">
                                        <div className="store-card-banner">
                                            <img
                                                src={outlet.image || "https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=800&q=80"}
                                                className="store-card-image"
                                                alt={outlet.name}
                                            />
                                            <div className="store-status-pill">
                                                <span className={`status-dot ${status.isOpen ? 'bg-success' : 'bg-danger'}`}></span>
                                                {status.isOpen ? 'Open Now' : 'Closed'}
                                            </div>
                                        </div>

                                        <div className="store-card-content">
                                            <h4>{outlet.name}</h4>

                                            <div className="store-info-row">
                                                <div className="store-icon-box">
                                                    <i className="fas fa-map-marker-alt"></i>
                                                </div>
                                                <p>{outlet.address}</p>
                                            </div>

                                            <div className="store-info-row">
                                                <div className="store-icon-box">
                                                    <i className="fas fa-phone"></i>
                                                </div>
                                                <p>{outlet.phone}</p>
                                            </div>

                                            <div className="store-info-row">
                                                <div className="store-icon-box">
                                                    <i className="fas fa-city"></i>
                                                </div>
                                                <p>{outlet.area?.city?.name}, {outlet.area?.name}</p>
                                            </div>

                                            <div className="store-info-row">
                                                <div className="store-icon-box">
                                                    <i className="fas fa-clock"></i>
                                                </div>
                                                <p>Open: {status.open} - Close: {status.close}</p>
                                            </div>
                                        </div>

                                        <div className="store-card-actions">
                                            {outlet.qrCode && (
                                                <a
                                                    href={outlet.qrCode}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="store-action-btn btn-order"
                                                >
                                                    <i className="fas fa-shopping-cart"></i>
                                                    Order Now
                                                </a>
                                            )}
                                            <a
                                                href={`https://maps.google.com/?q=${outlet.address}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="store-action-btn btn-directions"
                                            >
                                                <i className="fas fa-directions"></i>
                                                Directions
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {outlets.length === 0 && (
                        <div className="row justify-content-center">
                            <div className="col-12 text-center">
                                <div className="empty-stores-modern p-5">
                                    <i className="fas fa-store-slash mb-4"></i>
                                    <h3 className="ft-bold">No Stores Found</h3>
                                    <p className="text-muted">Currently, there are no store locations available in this area.</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

        </>
    )
}

export default Stores